// Single source of truth for sitemap structure AND breadcrumb trails.
// Adding a new service = add one entry to SERVICE_HUBS below.

export const SITE_URL = 'https://www.stringssg.com'

// Routes that must never appear in a sitemap.
// Mirrors (and replaces) the `exclude` list previously in next-sitemap.config.js.
// Patterns ending in * match by prefix.
export const EXCLUDED_ROUTES = [
    '/home*',
    '/history*',
    '/account*',
    '/notification*',
    '/services*',
    '/stripe-account*',
    '/user-activate',
    '/google-index',
    '/jobs*',
    '/rewards*',
    '/corporate/my-sessions',
    '/corporate/signup*',
    '/sg/ai-chat',
    '/faq/aircon-service-stringssg', // canonicalised to the aircon hub
    '/sitemap.xml',
    '/sitemaps*',
    '/team-job*',
    '/booking',
    '/profile*',       // service-provider profiles - intentionally excluded
    '/shared-job*',
    '/api/*',
    '/signin',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/service-provider-signup',
    '/404',
    '/500',
    '/_app',
    '/_document',
    '/_error',
]

// Ordered exactly as requested: main, aircon, personal trainer, handyman,
// domestic helper, then the rest. This order drives the <sitemapindex>.
export const SERVICE_HUBS = [
    {
        key: 'aircon-servicing-singapore',
        path: '/sg/aircon-servicing-singapore',
        title: 'Aircon Servicing Singapore',
        // Mirrors pages/sg/aircon-servicing-singapore/[slug].js, which renders
        // 100 new + 100 legacy "hvac-services" posts. Without wpIncludeLegacy
        // the pre-2023 posts were missing from the sitemap entirely.
        wpCategory: 'hvac-services',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/hvac-services'],
        localityPattern: '/sg/aircon-servicing-singapore/locality/{locality}',
    },
    {
        key: 'personal-trainer',
        path: '/sg/personal-trainer',
        title: 'Personal Trainer Singapore',
        wpCategory: 'personal-trainer-sg',
        blogPaths: ['/sg/blogs/health-fitness'],
        localityPattern: '/sg/personal-trainer/trainer-list/{locality}',
        // Trainer profile pages. Only directly-employed trainers with a
        // substantial bio are listed - see getProfileRoutes in lib/sitemap.
        // Freelance/unverified profiles and near-empty bios stay out of the
        // sitemap; they remain reachable via the trainer-list pages.
        profilePattern: '/profile/{handleName}',
        profileCategory: 'Fitness Trainer',
        profileMinBioChars: 400,
    },
    {
        key: 'handyman-singapore-services',
        path: '/sg/handyman-singapore-services',
        title: 'Handyman Services Singapore',
        // Mirrors pages/sg/handyman-singapore-services/[slug].js, which renders
        // 100 new + 100 legacy "repair-renovation" posts. Listing more than it
        // renders would put 404s in the sitemap.
        wpCategory: 'repair-renovation',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/repair-renovation'],
        localityPattern: null,
    },
    {
        key: 'part-time-cleaner-singapore',
        path: '/sg/part-time-cleaner-singapore',
        title: 'Part Time Cleaner Singapore',
        // Mirrors pages/sg/part-time-cleaner-singapore/[slug].js, which renders
        // 100 new + 100 legacy "house-keeping" posts. This was null, so none of
        // those posts were in the sitemap.
        wpCategory: 'house-keeping',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/house-keeping'],
        localityPattern: null,
    },
    {
        key: 'electrician-services-singapore',
        path: '/sg/electrician-services-singapore',
        title: 'Electrician Services Singapore',
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'plumber-singapore-services',
        path: '/sg/plumber-singapore-services',
        title: 'Plumber Services Singapore',
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'property-agent',
        path: '/sg/property-agent',
        title: 'Property Agent Singapore',
        wpCategory: 'housing',
        wpIncludeLegacy: true,
        blogPaths: ['/sg/blogs/housing'],
        localityPattern: null,
    },
    {
        key: 'financial-adviser-singapore',
        path: '/sg/financial-adviser-singapore',
        title: 'Financial Adviser Singapore',
        wpCategory: null,
        blogPaths: ['/sg/blogs/finance-insurance-investment'],
        localityPattern: null,
    },
]

// Priority / changefreq per depth tier.
export const TIERS = {
    home:     { priority: '1.0', changefreq: 'daily' },
    hub:      { priority: '1.0', changefreq: 'daily' },
    child:    { priority: '0.8', changefreq: 'weekly' },
    blog:     { priority: '0.6', changefreq: 'weekly' },
    locality: { priority: '0.5', changefreq: 'monthly' },
    profile:  { priority: '0.4', changefreq: 'monthly' },
    main:     { priority: '0.7', changefreq: 'monthly' },
}

// WordPress-backed dynamic routes that don't belong to a service hub.
// Mirrors pages/blogs/[slug].js (category "general").
export const MAIN_WP_SOURCES = [
    { basePath: '/blogs', wpCategory: 'general', includeLegacy: true },
]

export const getHubByKey = (key) => SERVICE_HUBS.find((h) => h.key === key)

// Longest-path match so nested hubs resolve correctly.
export const getHubForPath = (path) =>
    SERVICE_HUBS
        .filter((h) => path === h.path || path.startsWith(h.path + '/'))
        .sort((a, b) => b.path.length - a.path.length)[0] ?? null

export const isExcluded = (route) =>
    EXCLUDED_ROUTES.some((pattern) =>
        pattern.endsWith('*')
            ? route.startsWith(pattern.slice(0, -1))
            : route === pattern
    )
